import { Category, useWebgate } from "@xvia/webgate-connect";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { getCategories } from "../../apis/categories";
import { getDepartments, IDepartment } from "../../apis/department";
import { getApps, getTopCatalog } from "../../apis/relevant-catalog";
import { Catalog, search } from "../../apis/search-catalog";
import { getServerData } from "../../util/get-server-data";
import { resolveListMunicipalityContext } from "../../util/municipality-context";
import { List, ListProps } from "./list";
import { getProfileRecommendationsByUsername } from "../../apis/profile-recommendations";
import { mapProfileRecommendationsToCatalog } from "../../util/map-profile-recommendations";

const striptags = require("striptags");

export interface ListContainerProps {
  favoriteList?: string[];
  onFavoriteUpdate?: (slug: string, remove: boolean) => void;
  setSeverityAlert?: (severity: "success" | "error") => void;
  setOpenAlert?: (open: boolean) => void;
  setMessageAlert?: (message: string) => void;
}

export const ListContainer: FC<ListContainerProps> = ({
  favoriteList,
  onFavoriteUpdate,
  setSeverityAlert,
  setOpenAlert,
  setMessageAlert,
}) => {
  const { portalConnect, userInfo } = useWebgate();
  let history = useHistory();
  const location = useLocation();

  let { category, query, orgao, municipio } = useParams<{
    category: string;
    query: string;
    orgao: string;
    municipio: string;
  }>();

  const {
    codIbgeMunicipio: municipioCode,
    municipalityName: municipioNome,
    municipioQuerySuffix,
  } = useMemo(
    () => resolveListMunicipalityContext(municipio, location.search),
    [municipio, location.search]
  );

  const isRecomendadosPage =
    category === "recomendados" || query === "recomendados";

  const [categoryList, setCategoryList] = useState<Category[]>([]);
  const [departmentList, setDepartmentList] = useState<IDepartment[]>([]);
  const [catalogList, setCatalogList] = useState<Catalog[]>([]);
  const [searchTextFilter, setSearchTextFilter] = useState<string>(query);
  const [searchCategoryFilter, setSearchCategoryFilter] = useState<string>("");
  const [categorySlug, setCategorySlug] = useState<string>(category);
  const [page, setPage] = useState<number>(0);
  const [resultTotal, setResultTotal] = useState<number>();
  const [isMounted, setIsMounted] = useState(false);

  const currentPage = useMemo(() => page + 1, [page]);

  const openLink = useCallback(
    (catalog: Catalog) => {
      if (catalog.link.includes("/app/")) {
        portalConnect?.redirectTo(catalog.link);
        return;
      }

      const hasCatalogData = (catalog.catalogDataRow || []).length > 0;

      if (hasCatalogData) {
        const form = document.createElement("form");
        form.method = "POST";
        form.action = catalog.link;
        form.style.display = "none";

        catalog.catalogDataRow.forEach(
          ({ name, value }: { name: string; value: string }) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = name;
            input.value = value;
            form.appendChild(input);
          }
        );

        document.body.appendChild(form);
        form.submit();
        return;
      }

      globalThis.window.open(catalog.link, "_blank");
    },
    [portalConnect]
  );
  const onClickCard = useCallback(
    (categorySlug, slug) => {
      history.push(`/${categorySlug}/${slug}${municipioQuerySuffix}`);
    },
    [history, municipioQuerySuffix]
  );

  const onClickSearchCategory = useCallback(
    (categorySlug: string) => {
      history.push(`/list/${categorySlug}${municipioQuerySuffix}`);
      setCategorySlug(categorySlug);
    },
    [history, municipioQuerySuffix]
  );

  const onClickClearSearchCategory = useCallback(() => {
    setCategorySlug("");
    if (searchTextFilter)
      return history.push(`/search/${searchTextFilter}${municipioQuerySuffix}`);

    history.push(`/list/${municipioQuerySuffix}`);
  }, [history, searchTextFilter, municipioQuerySuffix]);

  const onClickSearch = useCallback(() => {
    if (categorySlug)
      return history.push(
        `/list/${categorySlug}/${searchTextFilter || ""}${municipioQuerySuffix}`
      );

    history.push(`/search/${searchTextFilter ?? ""}${municipioQuerySuffix}`);
  }, [searchTextFilter, history, categorySlug, municipioQuerySuffix]);

  const onClickClearFilter = useCallback(() => {
    setSearchTextFilter("");
    if (categorySlug)
      return history.push(`/list/${categorySlug}${municipioQuerySuffix}`);

    history.push(`/list/${municipioQuerySuffix}`);
  }, [categorySlug, history, municipioQuerySuffix]);

  const onDepartmentClick = useCallback(
    (departamentSlug: string) => {
      history.push(`/list/orgao/${departamentSlug}${municipioQuerySuffix}`);
    },
    [history, municipioQuerySuffix]
  );

  const onPaginate = useCallback((id: number) => {
    setPage(id - 1);
    if (globalThis.window !== undefined && globalThis.window.scrollY !== 0) {
      globalThis.window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    if (category === "mais-acessados" || query === "mais-acessados") {
      getTopCatalog(undefined, municipioCode).then((res) => {
        setCatalogList(res);
        setResultTotal(10);
      });
      return;
    }
    if (category === "acesso-rapido" || query === "acesso-rapido") {
      search(
        undefined,
        undefined,
        category,
        orgao,
        page * 10,
        true,
        municipioCode
      ).then((res) => {
        setCatalogList(res);
        setResultTotal(res[0]?.resultTotal);
      });
      return;
    }
    if (isRecomendadosPage) {
      if (!userInfo?.preferred_username) {
        setCatalogList([]);
        setResultTotal(0);
        return;
      }
      Promise.all([
        getProfileRecommendationsByUsername(userInfo.preferred_username),
        getApps(municipioCode),
      ])
        .then(([data, allCatalogList]) => {
          const mapped = mapProfileRecommendationsToCatalog(
            data,
            allCatalogList
          );
          setCatalogList(mapped);
          setResultTotal(mapped.length);
        })
        .catch((err) => {
          console.error("Error fetching profile recommendations:", err);
          setCatalogList([]);
          setResultTotal(0);
        });
      return;
    }
    search(undefined, query, category, orgao, page * 10, false, municipioCode)
      .then((res) => {
        setCatalogList(res);
        setResultTotal(res[0]?.resultTotal);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [
    query,
    category,
    orgao,
    page,
    municipioCode,
    userInfo?.preferred_username,
  ]);

  useEffect(() => {
    const parsedCategoryData = getServerData("categoryList", []);
    const parsedDepartmentData = getServerData("departmentList", []);
    const parsedCatalogData = getServerData("catalogList", []);

    // Em contexto municipal, sempre recarregamos categorias e órgãos
    // para garantir que a lista esteja no escopo do município selecionado.
    if (municipioCode) {
      getCategories(municipioCode)
        .then(setCategoryList)
        .catch((err) => {
          console.error("Error fetching category list:", err);
        });

      getDepartments(municipioCode)
        .then(setDepartmentList)
        .catch((err) => {
          console.error("Error fetching department list:", err);
        });

      return;
    }

    // Contexto estadual: reaproveita SSR quando disponível, caso contrário busca.
    if (!isMounted) {
      setIsMounted(true);

      if (parsedCategoryData.length === 0) {
        getCategories(municipioCode)
          .then(setCategoryList)
          .catch((err) => {
            console.error("Error fetching category list:", err);
          });
      } else {
        setCategoryList(parsedCategoryData);
      }

      if (parsedDepartmentData.length === 0) {
        getDepartments(municipioCode)
          .then(setDepartmentList)
          .catch((err) => {
            console.error("Error fetching department list:", err);
          });
      } else {
        setDepartmentList(parsedDepartmentData);
      }

      setCatalogList(parsedCatalogData);
    }
  }, [isMounted, municipioCode]);

  const newsProps: ListProps = {
    list: catalogList?.map((catalog) => {
      return {
        ...catalog,
        description: striptags(catalog.description),
      } as Catalog;
    }),
    onClick: onClickCard,
    onClickOnlineService: openLink,
    searchTextFilter,
    prefix: query ? " Palavra-chave:" : "",
    searchText: query,
    onSetSearchCategoryFilter: setSearchCategoryFilter,
    onClickSearchCategory,
    searchCategoryFilter,
    onClickSearch,
    onSetSearchValue: setSearchTextFilter,
    categoryList,
    categoryTitle: categoryList?.find((i) => {
      return i.slug === category;
    })?.name,
    departmentList,
    onDepartmentClick,
    orgaoTitle: departmentList?.find((i) => {
      return i.slug === orgao;
    })?.shortName,
    onPaginate,
    resultTotal,
    currentPage,
    onClickClearFilter,
    onClickClearSearchCategory,
    favoriteList,
    onFavoriteUpdate,
    setSeverityAlert,
    setOpenAlert,
    setMessageAlert,
    municipalityTitle: municipioNome,
  };

  return <List {...newsProps} />;
};
